import {EditorConfig, ExtensionType} from '@dudagroup/editor'
import React from 'react'
import {CustomViewCopyData, CustomViewImporter, CustomViewReindex} from './customViews/customView'
import {CustomViewFoundationCouncilImporter} from './customViews/customViewFoundationCouncilImporter'
import CustomViewExportEmployees from './customViews/customViewExportEmployees'
import CustomViewFilesUpload from './customViews/customViewFilesUpload'
import {getNavigationView} from './models/navigation/navigationView'
import {getGenericViewWithCustomBlockList, getPreviewLink} from './models/page/pageContentView'
import {getMediaView} from './models/media/mediaView'
import {CustomViewPurgecache} from './customViews/customViewPurgecache'
import {CustomViewEmailNotifications} from './customViews/customViewEmailNotifications'
import {CustomViewGlobalSearch} from './customViews/customViewGlobalSearch'
import {CommandPalette} from './customViews/commandPalette'
import SearchIcon from '@rsuite/icons/legacy/Search'

import Tag from '@rsuite/icons/legacy/Tag'
import ObjectUngroup from '@rsuite/icons/legacy/ObjectUngroup'
import Globe from '@rsuite/icons/legacy/Globe'
import Home from '@rsuite/icons/legacy/Home'
import Sitemap from '@rsuite/icons/legacy/Sitemap'
import AngleDoubleDown from '@rsuite/icons/legacy/AngleDoubleDown'
import FileImageO from '@rsuite/icons/legacy/FileImageO'
import FileTextO from '@rsuite/icons/legacy/FileTextO'
import Project from '@rsuite/icons/legacy/Project'
import IconImport from '@rsuite/icons/legacy/Import'
import NewspaperO from '@rsuite/icons/legacy/NewspaperO'
import Calendar from '@rsuite/icons/legacy/Calendar'
import Money from '@rsuite/icons/legacy/Money'
import Question2 from '@rsuite/icons/legacy/Question2'
import Film from '@rsuite/icons/legacy/Film'
import BlackTie from '@rsuite/icons/legacy/BlackTie'
import PeopleGroup from '@rsuite/icons/legacy/PeopleGroup'
import FileDownload from '@rsuite/icons/legacy/FileDownload'
import Building from '@rsuite/icons/legacy/Building'
import Briefcase from '@rsuite/icons/legacy/Briefcase'
import AddressBookO from '@rsuite/icons/legacy/AddressBookO'
import PreviewLinkCopy from './PreviewLinkCopy'
import ToolsIcon from '@rsuite/icons/Tools'
import GearIcon from '@rsuite/icons/Gear'

export const MODEL_MEDIA = 'media'
export const LANGUAGES = ['de', 'fr', 'en', 'it']

export const DEFAULT_LANGUAGE = (() => {
  const settings = document.getElementById('settings')
  if (settings?.textContent) {
    const {defaultLanguage} = JSON.parse(settings.textContent)
    return defaultLanguage as string
  }
  return 'de'
})()

export const config: EditorConfig = {
  navigationBar: {
    articlesActive: false,
    authorsActive: false,
    imageLibraryActive: false,
    commentsActive: false,
    navigationActive: false,
    pagesActive: false,
  },
  // "/" is the global search; the command palette (Ctrl/Cmd+Shift+K) is always mounted.
  homeView: <CustomViewGlobalSearch />,
  globalComponents: [<CommandPalette />],
  extensions: [
    {
      identifier: 'global-search',
      type: ExtensionType.CusomExtension,
      nameSingular: {de: 'Suche', en: 'Search'},
      namePlural: {de: 'Suche', en: 'Search'},
      icon: <SearchIcon />,
      view: <CustomViewGlobalSearch />,
    },
    {
      identifier: 'page',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Seite',
        en: 'Page',
      },
      namePlural: {
        de: 'Seiten',
        en: 'Pages',
      },
      icon: <FileTextO />,
      getContentView: getGenericViewWithCustomBlockList,
      getPreviewLink: getPreviewLink,
      deriveSlug: {
        instructions: 'derive from title',
        jsonPath: '$.content.title',
      },
      customColumns: [
        {
          headerTranslationKey: 'Link',
          getCustomViewContent: (content, contentDefaultLang) => {
            return <PreviewLinkCopy content={content} defaultLang={contentDefaultLang} />
          },
        },
      ],
    },
    {
      identifier: 'news',
      type: ExtensionType.ContentModelExtension,
      nameSingular: 'News',
      namePlural: 'News',
      icon: <NewspaperO />,
      getContentView: getGenericViewWithCustomBlockList,
      getPreviewLink: getPreviewLink,
      deriveSlug: {
        instructions: 'derive from title',
        jsonPath: '$.content.title',
      },
      customColumns: [
        {
          headerTranslationKey: 'Link',
          getCustomViewContent: (content, contentDefaultLang) => {
            return <PreviewLinkCopy content={content} defaultLang={contentDefaultLang} />
          },
        },
      ],
    },
    {
      identifier: 'categoryNews',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'News Kategorie',
        en: 'News Category',
      },
      namePlural: {
        de: 'News Kategorien',
        en: 'News Categories',
      },
      icon: <ObjectUngroup />,
    },
    {
      identifier: 'funding',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Förderinstrument',
        en: 'Funding',
      },
      namePlural: {
        de: 'Förderinstrumente',
        en: 'Fundings',
      },
      icon: <Money />,
      getContentView: getGenericViewWithCustomBlockList,
      getPreviewLink: getPreviewLink,
      deriveSlug: {
        instructions: 'derive from title',
        jsonPath: '$.content.name',
      },
      customColumns: [
        {
          headerTranslationKey: 'Link',
          getCustomViewContent: (content, contentDefaultLang) => {
            return <PreviewLinkCopy content={content} defaultLang={contentDefaultLang} />
          },
        },
      ],
    },
    {
      identifier: 'categoryFunding',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Förderinstrument Kategorie',
        en: 'Funding Category',
      },
      namePlural: {
        de: 'Förderinstrument Kategorien',
        en: 'Funding Categories',
      },
      icon: <ObjectUngroup />,
    },
    {
      identifier: 'event',
      type: ExtensionType.ContentModelExtension,
      nameSingular: 'Event',
      namePlural: 'Events',
      icon: <Calendar />,
      getContentView: getGenericViewWithCustomBlockList,
      getPreviewLink: getPreviewLink,
      deriveSlug: {
        instructions: 'derive from title',
        jsonPath: '$.content.title',
      },
      customColumns: [
        {
          headerTranslationKey: 'Link',
          getCustomViewContent: (content, contentDefaultLang) => {
            return <PreviewLinkCopy content={content} defaultLang={contentDefaultLang} />
          },
        },
      ],
    },
    {
      identifier: 'eventType',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Event Typ',
        en: 'Event Type',
      },
      namePlural: {
        de: 'Eventtypen',
        en: 'Event types',
      },
      icon: <ObjectUngroup />,
    },
    {
      identifier: 'impulseTopic',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Impulse Thema',
        en: 'Impulse Topic',
      },
      namePlural: {
        de: 'Impulsthemen',
        en: 'Impulse Topics',
      },
      icon: <FileTextO />,
      getContentView: getGenericViewWithCustomBlockList,
      getPreviewLink: getPreviewLink,
      deriveSlug: {
        instructions: 'derive from title',
        jsonPath: '$.content.title',
      },
      customColumns: [
        {
          headerTranslationKey: 'Link',
          getCustomViewContent: (content, contentDefaultLang) => {
            return <PreviewLinkCopy content={content} defaultLang={contentDefaultLang} />
          },
        },
      ],
    },
    {
      identifier: 'faq',
      type: ExtensionType.ContentModelExtension,
      nameSingular: 'FAQ',
      namePlural: 'FAQs',
      icon: <Question2 />,
      incomingReferences: [
        // direct faqList block
        {
          fromModel: 'page',
          filterPath: 'content__blocks__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von Seite',
            en: 'Used by Page',
          },
        },
        {
          fromModel: 'news',
          filterPath: 'content__blocks__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von News',
            en: 'Used by News',
          },
        },
        {
          fromModel: 'event',
          filterPath: 'content__blocks__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von Event',
            en: 'Used by Event',
          },
        },
        // faqList nested inside an accordion block
        {
          fromModel: 'page',
          filterPath: 'content__blocks__accordion__accordion__content__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von Seite (Accordion)',
            en: 'Used by Page (Accordion)',
          },
        },
        {
          fromModel: 'news',
          filterPath: 'content__blocks__accordion__accordion__content__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von News (Accordion)',
            en: 'Used by News (Accordion)',
          },
        },
        {
          fromModel: 'event',
          filterPath: 'content__blocks__accordion__accordion__content__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von Event (Accordion)',
            en: 'Used by Event (Accordion)',
          },
        },
        {
          fromModel: 'funding',
          filterPath: 'content__blocks__accordion__accordion__content__faqList__faqList__recordId',
          label: {
            de: 'Verwendet von Förderinstrument (Accordion)',
            en: 'Used by Funding (Accordion)',
          },
        },
        // faqListGroups block - top-level faqList
        {
          fromModel: 'page',
          filterPath: 'content__blocks__faqListGroups__faqList__recordId',
          label: {
            de: 'Verwendet von Seite (FAQ Gruppen)',
            en: 'Used by Page (FAQ Groups)',
          },
        },
        {
          fromModel: 'news',
          filterPath: 'content__blocks__faqListGroups__faqList__recordId',
          label: {
            de: 'Verwendet von News (FAQ Gruppen)',
            en: 'Used by News (FAQ Groups)',
          },
        },
        {
          fromModel: 'event',
          filterPath: 'content__blocks__faqListGroups__faqList__recordId',
          label: {
            de: 'Verwendet von Event (FAQ Gruppen)',
            en: 'Used by Event (FAQ Groups)',
          },
        },
        {
          fromModel: 'impulseTopic',
          filterPath: 'content__blocks__faqListGroups__faqList__recordId',
          label: {
            de: 'Verwendet von Impulse Thema (FAQ Gruppen)',
            en: 'Used by Impulse Topic (FAQ Groups)',
          },
        },
        {
          fromModel: 'funding',
          filterPath: 'content__blocks__faqListGroups__faqList__recordId',
          label: {
            de: 'Verwendet von Förderinstrument (FAQ Gruppen)',
            en: 'Used by Funding (FAQ Groups)',
          },
        },
        // faqListGroups block - faqList inside groups
        {
          fromModel: 'page',
          filterPath: 'content__blocks__faqListGroups__groups__faqList__recordId',
          label: {
            de: 'Verwendet von Seite (FAQ Gruppen - Gruppen)',
            en: 'Used by Page (FAQ Groups - Groups)',
          },
        },
        {
          fromModel: 'news',
          filterPath: 'content__blocks__faqListGroups__groups__faqList__recordId',
          label: {
            de: 'Verwendet von News (FAQ Gruppen - Gruppen)',
            en: 'Used by News (FAQ Groups - Groups)',
          },
        },
        {
          fromModel: 'event',
          filterPath: 'content__blocks__faqListGroups__groups__faqList__recordId',
          label: {
            de: 'Verwendet von Event (FAQ Gruppen - Gruppen)',
            en: 'Used by Event (FAQ Groups - Groups)',
          },
        },
        {
          fromModel: 'impulseTopic',
          filterPath: 'content__blocks__faqListGroups__groups__faqList__recordId',
          label: {
            de: 'Verwendet von Impulse Thema (FAQ Gruppen - Gruppen)',
            en: 'Used by Impulse Topic (FAQ Groups - Groups)',
          },
        },
        {
          fromModel: 'funding',
          filterPath: 'content__blocks__faqListGroups__groups__faqList__recordId',
          label: {
            de: 'Verwendet von Förderinstrument (FAQ Gruppen - Gruppen)',
            en: 'Used by Funding (FAQ Groups - Groups)',
          },
        },
      ],
    },
    {
      identifier: MODEL_MEDIA,
      type: ExtensionType.ContentModelExtension,
      nameSingular: 'Media',
      namePlural: 'Media',
      getContentView: getMediaView,

      customColumns: [
        {
          headerTranslationKey: 'Vorschau',
          previewPaths: [['content', 'media']],
          useAsPreviewInDetailView: true,
        },
        {
          headerTranslationKey: 'Link',
          fetchEnrichedContent: true,
          getCustomViewContent: (content, contentDefaultLang) => {
            return (
              <PreviewLinkCopy content={content} isMedia={true} defaultLang={contentDefaultLang} />
            )
          },
        },
      ],
      previewSize: 'big',
      icon: <FileImageO />,
      getPreviewLink: (token, language, recordData) => {
        const settings = document.getElementById('settings')
        const media = recordData?.content?.media?.[language]?.media
        if (settings?.textContent && media?.filename && media?.extension) {
          const {mediaPreviewUrl} = JSON.parse(settings.textContent)

          if (
            (recordData.meta.robotsMetaDirectives === 'indexFollow' ||
              recordData.meta.robotsMetaDirectives === 'indexNoFollow' ||
              !recordData.meta.robotsMetaDirectives) &&
            recordData.content.media[language].media
          ) {
            return `${mediaPreviewUrl}/media/${language}/${recordData.id}/${media.filename}${media.extension}`
          }

          return `${mediaPreviewUrl}/media-noindex/${language}/${recordData.id}/${media.filename}${media.extension}`
        }

        return null
      },
      disableDuplication: true,
    },
    {
      identifier: 'fileuploader',
      type: ExtensionType.CusomExtension,
      nameSingular: {
        de: 'Medien hochladen',
        en: 'Upload media',
      },
      namePlural: {
        de: 'Medien hochladen',
        en: 'Upload media',
      },
      icon: <FileImageO />,
      view: <CustomViewFilesUpload />,
    },
    {
      identifier: 'multimedia',
      type: ExtensionType.ContentModelExtension,
      nameSingular: 'Multimedia',
      namePlural: 'Multimedia',
      icon: <Film />,
      getPreviewLink: getPreviewLink,
      deriveSlug: {
        instructions: 'derive from title',
        jsonPath: '$.content.title',
      },
      customColumns: [
        {
          headerTranslationKey: 'Typ',
          fetchEnrichedContent: true,
          getCustomViewContent: (content) => {
            const mediaContent = content?.content?.content
            if (mediaContent?.video) {
              return (
                <span style={{display: 'inline-flex', alignItems: 'center', gap: '5px'}}>
                  <Film /> Video
                </span>
              )
            }
            if (mediaContent?.gallery) {
              return (
                <span style={{display: 'inline-flex', alignItems: 'center', gap: '5px'}}>
                  <FileImageO /> Galerie
                </span>
              )
            }
            return <></>
          },
        },
        {
          headerTranslationKey: 'Link',
          getCustomViewContent: (content, contentDefaultLang) => {
            return <PreviewLinkCopy content={content} defaultLang={contentDefaultLang} />
          },
        },
      ],
    },
    {
      identifier: 'categoryMultimedia',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Multimedia Kategorie',
        en: 'Multimedia Category',
      },
      namePlural: {
        de: 'Multimedia Kategorien',
        en: 'Multimedia Categories',
      },
      icon: <ObjectUngroup />,
    },
    {
      identifier: 'tag',
      type: ExtensionType.ContentModelExtension,
      nameSingular: 'Tag',
      namePlural: 'Tags',
      icon: <Tag />,
    },
    {
      identifier: 'contact',
      type: ExtensionType.ContentModelExtension,
      nameSingular: {
        de: 'Kontakt',
        en: 'Contact',
      },
      namePlural: {
        de: 'Kontakte',
        en: 'Contacts',
      },
      icon: <AddressBookO />,
    },
    {
      type: ExtensionType.Folder,
      label: {
        de: 'Stiftungsrat',
        en: 'Foundation Council',
      },
      icon: <BlackTie />,
      children: [
        {
          identifier: 'foundationCouncilMember',
          type: ExtensionType.ContentModelExtension,
          namePlural: 'Foundation Council Members',
          nameSingular: 'Foundation Council Member',
          hidden: false,
          icon: <BlackTie />,
        },
        {
          identifier: 'foundationCouncilImport',
          type: ExtensionType.ContentModelExtension,
          namePlural: 'Foundation Council Imports',
          nameSingular: 'Foundation Council Import',
          hidden: false,
          icon: <Project />,
        },
        {
          identifier: 'foundationCouncilImporter',
          type: ExtensionType.CusomExtension,
          nameSingular: 'Foundation Council Auto Importer',
          namePlural: 'Foundation Council Auto Importer',
          view: <CustomViewFoundationCouncilImporter />,
          icon: <IconImport />,
        },
      ],
    },
    {
      type: ExtensionType.Folder,
      label: {
        de: 'Panel Members',
        en: 'Panel Members',
      },
      icon: <BlackTie />,
      children: [
        {
          identifier: 'panelMember',
          type: ExtensionType.ContentModelExtension,
          namePlural: 'Panel Members',
          nameSingular: 'Panel Member',
          hidden: false,
          icon: <BlackTie />,
        },
        {
          identifier: 'panelMemberFunction',
          type: ExtensionType.ContentModelExtension,
          namePlural: 'Panel Member Functions',
          nameSingular: 'Panel Member Function',
          hidden: false,
          icon: <Project />,
        },
        {
          identifier: 'panelMemberEvaluation',
          type: ExtensionType.ContentModelExtension,
          namePlural: 'Evaluation Bodies',
          nameSingular: 'Panel Member Evaluation body',
          hidden: false,
          icon: <Project />,
        },
        {
          identifier: 'panelMemberImport',
          type: ExtensionType.ContentModelExtension,
          namePlural: 'Panel Member Imports',
          nameSingular: 'Panel Member Import',
          hidden: false,
          icon: <Project />,
        },
        {
          identifier: 'importer',
          type: ExtensionType.CusomExtension,
          nameSingular: 'Panel Member Auto Importer',
          namePlural: 'Panel Member Auto Importer',
          view: <CustomViewImporter />,
          icon: <IconImport />,
        },
      ],
    },
    {
      type: ExtensionType.Folder,
      label: {
        de: 'Mitarbeitende',
        en: 'Employees',
      },
      icon: <PeopleGroup />,
      children: [
        {
          identifier: 'employee',
          type: ExtensionType.ContentModelExtension,
          customColumns: [
            {
              headerTranslationKey: 'Vorschau',
              previewPaths: [['content', 'image']],
              useAsPreviewInDetailView: true,
            },
          ],
          previewSize: 'big',
          nameSingular: {
            de: 'Mitarbeitende',
            en: 'Employee',
          },
          namePlural: {
            de: 'Mitarbeitende',
            en: 'Employees',
          },
          icon: <PeopleGroup />,
        },
        {
          identifier: 'job',
          type: ExtensionType.ContentModelExtension,
          nameSingular: {
            de: 'Job',
            en: 'Job',
          },
          namePlural: {
            de: 'Jobs',
            en: 'Jobs',
          },
          icon: <Briefcase />,
        },
        {
          identifier: 'employeeCsvExport',
          type: ExtensionType.CusomExtension,
          nameSingular: {
            de: 'Mitarbeitende CSV Export',
            en: 'Employee CSV Export',
          },
          namePlural: {
            de: 'Mitarbeitende CSV Export',
            en: 'Employees CSV Export',
          },
          icon: <FileDownload />,
          view: <CustomViewExportEmployees />,
        },
        {
          identifier: 'division',
          type: ExtensionType.ContentModelExtension,
          nameSingular: {
            de: 'Abteilung',
            en: 'Division',
          },
          namePlural: {
            de: 'Abteilungen',
            en: 'Divisions',
          },
          icon: <Building />,
        },
        {
          identifier: 'organizationUnit',
          type: ExtensionType.ContentModelExtension,
          nameSingular: {
            de: 'Org. Einheit',
            en: 'Org. unit',
          },
          namePlural: {
            de: 'Org. Einheiten',
            en: 'Org. units',
          },
          icon: <Building />,
        },
      ],
    },
    {
      type: ExtensionType.Folder,
      label: {
        de: 'Webseiten Einstellungen',
        en: 'Website Settings',
      },
      icon: <GearIcon />,
      children: [
        {
          identifier: 'nationalResearchProgramme',
          type: ExtensionType.ContentModelExtension,
          nameSingular: {
            de: 'Peer',
            en: 'Peer',
          },
          namePlural: {
            de: 'Peers',
            en: 'Peers',
          },
          icon: <ObjectUngroup />,
        },
        {
          identifier: 'domain',
          type: ExtensionType.ContentModelExtension,
          nameSingular: 'Domain',
          namePlural: 'Domains',
          icon: <Globe />,
        },
        {
          identifier: 'website',
          type: ExtensionType.ContentModelExtension,
          nameSingular: 'Website',
          namePlural: 'Websites',
          icon: <Home />,
        },
        {
          identifier: 'navigation',
          type: ExtensionType.ContentModelExtension,
          nameSingular: 'Navigation',
          namePlural: 'Navigations',
          icon: <Sitemap />,
          getContentView: getNavigationView,
        },
        {
          identifier: 'footer',
          type: ExtensionType.ContentModelExtension,
          nameSingular: 'Footer',
          namePlural: 'Footers',
          icon: <AngleDoubleDown />,
        },
      ],
    },
    {
      type: ExtensionType.Folder,
      label: {
        de: 'Maintenance',
        en: 'Maintenance',
      },
      icon: <ToolsIcon />,
      children: [
        {
          identifier: 'reindex',
          type: ExtensionType.CusomExtension,
          nameSingular: 'Reindex',
          namePlural: 'Reindex',
          view: <CustomViewReindex />,
        },
        {
          identifier: 'cache',
          type: ExtensionType.CusomExtension,
          nameSingular: 'Cache',
          namePlural: 'Cache',
          view: <CustomViewPurgecache />,
        },
        {
          identifier: 'copyData',
          type: ExtensionType.CusomExtension,
          nameSingular: {
            de: 'Daten kopieren',
            en: 'Copy Data',
          },
          namePlural: {
            de: 'Daten kopieren',
            en: 'Copy Data',
          },
          view: <CustomViewCopyData />,
        },
        {
          identifier: 'emailNotifications',
          type: ExtensionType.CusomExtension,
          nameSingular: {
            de: 'E-Mail-Benachrichtigungen',
            en: 'Email Notifications',
          },
          namePlural: {
            de: 'E-Mail-Benachrichtigungen',
            en: 'Email Notifications',
          },
          view: <CustomViewEmailNotifications />,
        },
      ],
    },
  ],
}
